import type React from 'react';
import { type ReactNode, useEffect, useRef } from 'react';
import { useIsDashboardMode } from '../../hooks/useIsDashboardMode';
import { useCanSplitLayout } from '../hooks/useCanSplitLayout';
import { usePanelStore } from '../hooks/usePanelStore';
import { MAX_SPLIT_RATIO, MIN_SPLIT_RATIO, useSplitRatio } from '../hooks/useSplitRatio';
import {
  ChatColumn,
  CollapsedPanelTab,
  CollapsedPanelTabBadge,
  CollapsedPanelTabLabel,
  MainPanelColumn,
  SplitHandle,
  SplitRoot,
} from './dualPanel.styles';
import { MainPanel } from './MainPanel';

type DualPanelLayoutProps = {
  children: ReactNode;
};

export const DualPanelLayout: React.FC<DualPanelLayoutProps> = ({ children }) => {
  const { panels, isCollapsed, setCollapsed, animateLayout } = usePanelStore();
  const canSplit = useCanSplitLayout();
  const isDashboard = useIsDashboardMode();
  // Last *committed* mode, to tell a real panel move from a mode change (see below). Updated in an
  // effect rather than during render: StrictMode renders twice, and the second pass would already
  // see the new value and report no change.
  const wasDashboardRef = useRef(isDashboard);
  const splitRef = useRef<HTMLDivElement>(null);
  const { ratio, isDragging, onHandlePointerDown, onHandleKeyDown, resetRatio } = useSplitRatio(splitRef);
  const modeChanged = isDashboard !== wasDashboardRef.current;
  useEffect(() => {
    wasDashboardRef.current = isDashboard;
  });

  if (!canSplit) {
    return <>{children}</>;
  }

  const hasPanels = panels.length > 0;
  const animateColumns = animateLayout && !isDashboard && !modeChanged;

  // A dashboard panel pins the dual panel open (it must always be visible and can't collapse).
  const hasDashboardPanel = panels.some((panel) => panel.source === 'dashboard');
  const isOpen = hasDashboardPanel || (hasPanels && !isCollapsed);

  return (
    <SplitRoot ref={splitRef} $open={isOpen} $animate={animateColumns && !isDragging} $ratio={ratio}>
      <MainPanelColumn $open={isOpen} aria-hidden={!isOpen}>
        <MainPanel />
      </MainPanelColumn>
      {isOpen && (
        <SplitHandle
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize the dashboard and conversation columns"
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={Math.round(MIN_SPLIT_RATIO * 100)}
          aria-valuemax={Math.round(MAX_SPLIT_RATIO * 100)}
          tabIndex={0}
          $dragging={isDragging}
          onPointerDown={onHandlePointerDown}
          onKeyDown={onHandleKeyDown}
          onDoubleClick={resetRatio}
          title="Drag to resize — double-click to reset"
        />
      )}
      <ChatColumn $hasPanels={hasPanels}>{children}</ChatColumn>
      {!hasDashboardPanel && hasPanels && isCollapsed && (
        <CollapsedPanelTab type="button" onClick={() => setCollapsed(false)} title="Expand panel">
          <CollapsedPanelTabLabel>{panels.length === 1 ? panels[0].title : 'Panels'}</CollapsedPanelTabLabel>
          <CollapsedPanelTabBadge>{panels.length}</CollapsedPanelTabBadge>
        </CollapsedPanelTab>
      )}
    </SplitRoot>
  );
};
